import React, { useState } from 'react';
import {
  Building2,
  Database,
  ShieldCheck,
  Boxes,
  Users,
  Cpu,
  Layers,
  Globe2,
  CheckCircle2,
  ArrowRight,
  Lock,
  Workflow,
  Server,
  Activity,
  Zap,
  Terminal,
  FileCheck2,
  HardDrive,
  MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';

const EnterpriseSolutions = () => {
  const [activeTab, setActiveTab] = useState(0);

  const interactiveSolutions = [
    {
      id: 'core',
      title: 'Custom Enterprise Software Core',
      badge: 'Mission Critical',
      icon: Building2,
      subtitle: 'High-Concurrency Business Platforms',
      description: 'Bespoke web applications and multi-tenant SaaS platforms built for millions of daily active users with ultra-low latency.',
      features: [
        'Multi-Tenant Architecture & Data Isolation',
        'High-Throughput Distributed Caching (Redis/Memcached)',
        'Event-Driven Microservices with Apache Kafka',
        'Automated Horizontal Auto-Scaling Pools'
      ],
      techStack: ['Node.js', 'Next.js 15', 'PostgreSQL', 'Docker', 'Kubernetes'],
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&q=80'
    },
    {
      id: 'erp',
      title: 'Enterprise Resource Planning (ERP)',
      badge: 'Operational Nerve Center',
      icon: Workflow,
      subtitle: 'Unified Supply Chain & Financial Ledger',
      description: 'Centralize cross-department operations starting from raw material procurement down to automated global logistics and financial accounting.',
      features: [
        'Real-Time Multi-Warehouse Inventory Sync',
        'Automated General Ledger & Tax Compliance',
        'Procurement & Purchase Order Automation',
        'Integrated HRMS & Payroll Modules'
      ],
      techStack: ['Python', 'FastAPI', 'PostgreSQL', 'SAP API Sync', 'React'],
      image: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80'
    },
    {
      id: 'crm',
      title: 'Intelligent CRM & Telemetry',
      badge: '360° Customer Intelligence',
      icon: Users,
      subtitle: 'Predictive Sales Pipelines & Omnichannel Support',
      description: 'Engineered with machine learning lead scoring, automated deal routing, and instant WhatsApp/Email customer telemetry.',
      features: [
        '360-Degree Customer Journey Profiling',
        'Automated Sales Pipeline & Deal Telemetry',
        'AI-Powered Predictive Lead Scoring',
        'Omnichannel Support Ticketing Sync'
      ],
      techStack: ['React', 'Node.js', 'Elasticsearch', 'Twilio API', 'GraphQL'],
      image: 'https://images.unsplash.com/photo-1556761175-5973dc0f32e7?w=800&q=80'
    },
    {
      id: 'modernization',
      title: 'Legacy System Modernization',
      badge: 'Zero Downtime',
      icon: Server,
      subtitle: 'Re-platforming Monoliths to Microservices',
      description: 'Safely refactor outdated legacy databases and COBOL/Java monoliths into cloud-native microservice containers.',
      features: [
        'Strangler Fig Migration Pattern Execution',
        'Zero-Downtime Database Re-platforming',
        'REST & GraphQL Wrapper Architecture',
        'Containerization with Docker & Kubernetes'
      ],
      techStack: ['Go', 'Docker', 'Kubernetes', 'AWS EKS', 'Kong API Gateway'],
      image: 'https://images.unsplash.com/photo-1558494949-ef010cbdcc31?w=800&q=80'
    },
    {
      id: 'security',
      title: 'Bank-Grade Security & Governance',
      badge: 'SOC2 Certified',
      icon: ShieldCheck,
      subtitle: 'Zero-Trust Architecture & Data Protection',
      description: 'Impenetrable enterprise security layers enforcing SAML/OAuth2 Single Sign-On (SSO) and AES-256 field-level encryption.',
      features: [
        'SAML 2.0 & Okta / Azure AD Single Sign-On (SSO)',
        'Granular Role-Based Access Control (RBAC)',
        'AES-256 Data Encryption at Rest & TLS 1.3 in Transit',
        'Continuous Vulnerability Scanning & Audit Logging'
      ],
      techStack: ['OAuth 2.0', 'OpenID Connect', 'Vault', 'Cloudflare Enterprise'],
      image: 'https://images.unsplash.com/photo-1563986768609-322da13575f3?w=800&q=80'
    }
  ];

  const executionSteps = [
    { num: '01', title: 'Architecture & Security Audit', desc: 'In-depth review of existing IT infrastructure, bottlenecks, and security posture.' },
    { num: '02', title: 'Sandbox Blueprint & Proof of Concept', desc: 'Rapid prototyping of microservices and schema definitions in an isolated sandbox.' },
    { num: '03', title: 'Phased Migration & Integration', desc: 'Zero-downtime deployment executed in controlled sprint releases.' },
    { num: '04', title: '24/7 SLA & Performance Management', desc: 'Continuous monitoring, load balancing, and dedicated engineering support.' }
  ];

  const enterpriseProps = [
    'Free Architectural & IT Audit',
    'Strict Non-Disclosure Agreement (NDA)',
    'Dedicated Scrum Senior Engineers',
    '99.999% SLA Uptime Guarantee'
  ];

  return (
    <div className="min-h-screen bg-slate-950 text-left pt-20 text-slate-100">

      {/* Enterprise Command Center Hero Header */}
      <section className="py-24 bg-gradient-to-b from-slate-900 via-slate-950 to-slate-950 relative overflow-hidden border-b border-slate-800/80">
        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Header Info */}
            <div className="lg:col-span-7">
              <div className="inline-flex items-center space-x-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full px-4 py-1.5 mb-6">
                <Activity className="w-4 h-4 text-cyan-400 animate-pulse" />
                <span className="text-cyan-400 text-sm font-semibold uppercase tracking-wider">
                  Next-Gen Enterprise Architecture
                </span>
              </div>

              <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-6 leading-tight">
                Commanding Scale with <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-orange-400 bg-clip-text text-transparent">Mission-Critical</span> Software
              </h1>

              <p className="text-slate-300 text-base md:text-xl leading-relaxed mb-8">
                We engineer fault-tolerant enterprise software, custom ERP/CRM engines, and cloud microservices designed for high concurrency, zero downtime, and bank-grade governance.
              </p>

              <div className="flex flex-wrap gap-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Request Executive Audit
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="#interactive-blueprint">
                  <Button variant="outline" className="border-slate-700 text-slate-300 hover:bg-slate-800 px-8 py-6 text-base font-semibold rounded-xl">
                    Explore Blueprint
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Live Telemetry Graphic Box */}
            <div className="lg:col-span-5">
              <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-6 shadow-2xl relative overflow-hidden backdrop-blur-xl">

                {/* Simulated Server Bar */}
                <div className="flex items-center justify-between pb-4 border-b border-slate-800 mb-6">
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500 animate-ping" />
                    <span className="text-sm font-mono text-emerald-400 font-bold">ENTERPRISE CLUSTER: ONLINE</span>
                  </div>
                  <span className="text-[11px] font-mono text-slate-400">99.999% SLA</span>
                </div>

                {/* Telemetry Stats Grid */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-xl">
                    <div className="text-sm text-slate-400 mb-1">Global Latency</div>
                    <div className="text-2xl font-extrabold text-cyan-400 font-mono">14.2 ms</div>
                  </div>
                  <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-xl">
                    <div className="text-sm text-slate-400 mb-1">Daily Requests</div>
                    <div className="text-2xl font-extrabold text-orange-400 font-mono">48.2M</div>
                  </div>
                  <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-xl">
                    <div className="text-sm text-slate-400 mb-1">Security Audit</div>
                    <div className="text-2xl font-extrabold text-emerald-400 font-mono">PASSED</div>
                  </div>
                  <div className="bg-slate-950/80 border border-slate-800 p-4 rounded-xl">
                    <div className="text-sm text-slate-400 mb-1">Active Nodes</div>
                    <div className="text-2xl font-extrabold text-blue-400 font-mono">128 EKS</div>
                  </div>
                </div>

                {/* Terminal Line */}
                <div className="bg-slate-950 p-3 rounded-xl border border-slate-800 font-mono text-sm text-slate-400 flex items-center gap-2">
                  <Terminal className="w-4 h-4 text-cyan-400" />
                  <span>kubectl get pods -n prod --status=Running</span>
                </div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Interactive Enterprise Solution Navigator Concept */}
      <section id="interactive-blueprint" className="py-24 bg-slate-950">
        <div className="container mx-auto px-6">

          <div className="text-center mb-16 max-w-3xl mx-auto">
            <div className="inline-block bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5 mb-4">
              <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                Interactive Solution Navigator
              </span>
            </div>
            <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4 leading-tight">
              Enterprise <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Capability Blueprint</span>
            </h2>
            <p className="text-slate-300 text-base md:text-lg leading-relaxed">
              Select an enterprise solution category below to inspect its detailed architecture, technical stack, and feature specs.
            </p>
          </div>

          {/* Interactive Split Navigator */}
          <div className="grid lg:grid-cols-12 gap-8 items-start max-w-6xl mx-auto">

            {/* Left Vertical Menu */}
            <div className="lg:col-span-5 space-y-3">
              {interactiveSolutions.map((item, idx) => (
                <button
                  key={item.id}
                  onClick={() => setActiveTab(idx)}
                  className={`w-full p-5 rounded-2xl border text-left transition-all duration-300 flex items-center justify-between group ${activeTab === idx
                      ? 'bg-slate-900 border-cyan-500/80 shadow-xl shadow-cyan-500/10'
                      : 'bg-slate-900/40 border-slate-800/80 hover:bg-slate-900/70 hover:border-slate-700'
                    }`}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-300 ${activeTab === idx
                        ? 'bg-gradient-to-br from-cyan-500 to-blue-600 text-white scale-110'
                        : 'bg-slate-800 text-slate-400 group-hover:text-white'
                      }`}>
                      <item.icon className="w-6 h-6" />
                    </div>
                    <div>
                      <h4 className={`text-base font-bold transition-colors ${activeTab === idx ? 'text-white' : 'text-slate-300 group-hover:text-white'
                        }`}>
                        {item.title}
                      </h4>
                      <span className="text-sm text-slate-400">{item.subtitle}</span>
                    </div>
                  </div>

                  <ArrowRight className={`w-4 h-4 transition-transform ${activeTab === idx ? 'text-cyan-400 translate-x-1' : 'text-slate-600 opacity-0 group-hover:opacity-100'
                    }`} />
                </button>
              ))}
            </div>

            {/* Right Interactive Detail Showcase Panel */}
            <div className="lg:col-span-7">
              <div className="bg-slate-900 border border-slate-800 rounded-3xl overflow-hidden shadow-2xl p-8 transition-all duration-500">

                {/* Panel Image Showcase */}
                <div className="relative h-56 rounded-2xl overflow-hidden mb-6 group">
                  <img
                    src={interactiveSolutions[activeTab].image}
                    alt={interactiveSolutions[activeTab].title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/40 to-transparent" />
                  <div className="absolute top-4 left-4 bg-slate-900/90 backdrop-blur-md border border-cyan-500/30 text-cyan-300 text-sm font-bold px-3.5 py-1.5 rounded-full">
                    {interactiveSolutions[activeTab].badge}
                  </div>
                </div>

                {/* Content */}
                <h3 className="text-2xl font-extrabold text-white mb-3">
                  {interactiveSolutions[activeTab].title}
                </h3>
                <p className="text-slate-300 text-sm leading-relaxed mb-6">
                  {interactiveSolutions[activeTab].description}
                </p>

                {/* Features Checklist */}
                <div className="space-y-3 mb-8">
                  <div className="text-sm text-slate-400 font-bold uppercase tracking-wider">Architecture Highlights</div>
                  {interactiveSolutions[activeTab].features.map((feat, i) => (
                    <div key={i} className="flex items-center text-sm text-slate-200 font-medium">
                      <CheckCircle2 className="w-4 h-4 text-orange-400 mr-2.5 flex-shrink-0" />
                      <span>{feat}</span>
                    </div>
                  ))}
                </div>

                {/* Tech Stack Pills */}
                <div className="pt-4 border-t border-slate-800 flex items-center justify-between">
                  <div className="flex flex-wrap gap-2">
                    {interactiveSolutions[activeTab].techStack.map((tech, i) => (
                      <span key={i} className="bg-slate-800 border border-slate-700 text-cyan-300 text-sm px-3 py-1 rounded-lg font-mono">
                        {tech}
                      </span>
                    ))}
                  </div>

                  <a href="/contact" className="text-sm font-bold text-orange-400 hover:text-orange-300 flex items-center gap-1">
                    Deploy Solution
                    <ArrowRight className="w-4 h-4" />
                  </a>
                </div>

              </div>
            </div>

          </div>

        </div>
      </section>

      {/* 4-Step Enterprise Execution Pipeline */}
      <section className="py-20 bg-slate-900/60 border-t border-slate-800">
        <div className="container mx-auto px-6 max-w-5xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-extrabold text-white mb-3">
              Enterprise Execution Roadmap
            </h2>
            <p className="text-slate-400 text-sm max-w-xl mx-auto">
              How we take your legacy IT systems or greenfield ideas from audit to continuous 24/7 SLA operation.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {executionSteps.map((step, i) => (
              <div key={i} className="bg-slate-950 border border-slate-800 rounded-2xl p-6 relative">
                <span className="text-3xl font-black text-cyan-400 font-mono block mb-3">{step.num}</span>
                <h4 className="text-base font-bold text-white mb-2">{step.title}</h4>
                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Full-Width Executive Consultation Showcase Section */}
      <section className="py-24 bg-gradient-to-r from-slate-900 via-gray-950 to-slate-900 relative overflow-hidden border-t border-slate-800/80 w-full">
        {/* Mesh Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1f293715_1px,transparent_1px),linear-gradient(to_bottom,#1f293715_1px,transparent_1px)] bg-[size:4rem_4rem] pointer-events-none" />

        <div className="container mx-auto px-6 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-center">

            {/* Left Column Text & Value Props */}
            <div className="lg:col-span-7 text-left space-y-6">
              <div className="inline-flex items-center space-x-2 bg-orange-500/10 border border-orange-500/20 rounded-full px-4 py-1.5">
                <span className="text-orange-400 text-sm font-semibold uppercase tracking-wider">
                  Executive Consultation & NDA
                </span>
              </div>

              <h2 className="text-3xl md:text-5xl font-extrabold text-white leading-tight">
                Schedule Your Executive <span className="bg-gradient-to-r from-orange-400 via-amber-400 to-cyan-400 bg-clip-text text-transparent">Enterprise Consultation</span>
              </h2>

              <p className="text-slate-300 text-base md:text-lg leading-relaxed">
                Engage directly with our senior enterprise software architects to evaluate your existing IT stack, design custom ERP/CRM roadmaps, and establish bank-grade security protocols.
              </p>

              {/* 4 Enterprise Value Props Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                {enterpriseProps.map((prop, idx) => (
                  <div key={idx} className="flex items-center space-x-3 bg-slate-900/80 border border-slate-800 p-3.5 rounded-xl">
                    <CheckCircle2 className="w-4 h-4 text-cyan-400 flex-shrink-0" />
                    <span className="text-sm font-semibold text-slate-200">{prop}</span>
                  </div>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-4 pt-4">
                <a href="/contact">
                  <Button className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white px-8 py-6 text-base font-bold rounded-xl shadow-lg hover:shadow-orange-500/30 transition-all duration-300 flex items-center gap-2">
                    Book Executive Meeting
                    <ArrowRight className="w-5 h-5" />
                  </Button>
                </a>
                <a href="https://wa.me/918796118101?text=Hello!%20I%20want%20to%20discuss%20an%20Enterprise%20solution." target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" className="border-slate-700 text-emerald-400 hover:bg-slate-800 px-8 py-6 text-base font-semibold rounded-xl flex items-center gap-2">
                    <MessageSquare className="w-5 h-5" />
                    Talk To Expert (+91 8796 118 101)
                  </Button>
                </a>
              </div>
            </div>

            {/* Right Column Interactive Enterprise Console Display */}
            <div className="lg:col-span-5">
              <div className="bg-slate-900/90 border border-slate-800 rounded-3xl p-8 shadow-2xl relative backdrop-blur-xl">

                <div className="text-sm font-mono uppercase tracking-wider text-cyan-400 mb-4 flex items-center justify-between">
                  <span>ENTERPRISE ARCHITECTURE HUB</span>
                  <span className="text-emerald-400">ACTIVE SESSION</span>
                </div>

                <div className="space-y-4">
                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-400 font-semibold">Infrastructure SLA</div>
                      <div className="text-lg font-bold text-white">99.999% Guaranteed Uptime</div>
                    </div>
                    <ShieldCheck className="w-6 h-6 text-emerald-400" />
                  </div>

                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-400 font-semibold">Security Framework</div>
                      <div className="text-lg font-bold text-white">SOC 2 Type II & ISO 27001</div>
                    </div>
                    <Lock className="w-6 h-6 text-orange-400" />
                  </div>

                  <div className="bg-slate-950 p-4 rounded-xl border border-slate-800 flex items-center justify-between">
                    <div>
                      <div className="text-sm text-slate-400 font-semibold">Engineering Team</div>
                      <div className="text-lg font-bold text-white">Dedicated Senior Scrum Team</div>
                    </div>
                    <Users className="w-6 h-6 text-cyan-400" />
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t border-slate-800 text-center">
                  <p className="text-sm text-slate-400">
                    Need immediate technical advisory? Response within 2 hours guaranteed under NDA.
                  </p>
                </div>

              </div>
            </div>

          </div>
        </div>

        {/* Ambient Glow */}
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl pointer-events-none" />
      </section>

    </div>
  );
};

export default EnterpriseSolutions;